import React, { useEffect } from 'react';
import Webcam from 'react-webcam';
import { useRef, useState, useCallback } from 'react';
import { Box, Button, Typography, IconButton, Stack } from '@mui/material';

import * as tf from '@tensorflow/tfjs';
import '@tensorflow/tfjs-backend-webgl';
import * as facemesh from '@tensorflow-models/face-landmarks-detection';
import * as blazeface from '@tensorflow-models/blazeface';

import { AiFillCamera } from 'react-icons/ai';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Link } from 'react-router-dom';
import { MdCameraAlt, MdOutlineClose } from 'react-icons/md';
import Loader from '../../global/Loader';
import { useTranslation } from 'react-i18next';

const BASE_URL = process.env.REACT_APP_BASE_URL;

function WebcamCaptureDocWithSelfie({ iconButtonHandler, handleDocSelfieContinue }) {
  const webcamRef = useRef(null);
  const { t } = useTranslation();
  const cameraSoundRef = useRef(null);
  const [mirrorValue, setMirrorValue] = useState(false);
  const [openloader, setOpenloader] = useState(false);

  const selfioeWithDocRef = useRef();
  const selfioeWithDocCanvasRef = useRef();
  const [gettingCapture, setGetingCapture] = useState(false);
  const [docCameraInterVal, setdocCameraInterVal] = useState(null);

  const [imgSrc, setImgSrc] = useState(null); // Captured image state for document with selfie window

  const capture = useCallback(
    (docCameraInterVal) => {
      if (docCameraInterVal) {
        clearInterval(docCameraInterVal.docCameraInterValId);
      }
      const imageSrc = selfioeWithDocRef.current.getScreenshot();
      setImgSrc(imageSrc);

      if (cameraSoundRef.current) {
        cameraSoundRef.current.play(); // camera sound, when clicked image
      }
    },
    [selfioeWithDocRef, setImgSrc]
  );

  const [open, setOpen] = useState(false);
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Document with selfie camera
  const recBorder = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="200" height="125" viewBox="0 0 200 125">
      <g
        id="Rectangle_244"
        data-name="Rectangle 244"
        fill="none"
        stroke="#f37021"
        stroke-width="2"
        stroke-dasharray="5 10"
      >
        <rect width="200" height="125" rx="10" stroke="none" />
        <rect x="1" y="1" width="198" height="123" rx="9" fill="none" />
      </g>
    </svg>
  );

  const openCvReady = async (userMedia) => {
    setOpenloader(true);
    if (selfioeWithDocRef.current) {
      const selfioeWithDocCan = document.getElementById('canvas_output');
      const selfioeWithDocVideo = document.getElementById('selfioeWithDocVideo');

      const mediaWidth = userMedia.getTracks()[0].getSettings().width;
      const mediaHeight = userMedia.getTracks()[0].getSettings().height;
      const mediaVideo = selfioeWithDocRef.current.video;

      selfioeWithDocCan.width = mediaWidth;
      selfioeWithDocCan.height = mediaHeight;
      selfioeWithDocVideo.width = mediaWidth;
      selfioeWithDocVideo.height = mediaHeight;
      const ctx = selfioeWithDocCan.getContext('2d');
      selfioeWithDocRef.current.video.width = mediaWidth;
      selfioeWithDocRef.current.video.height = mediaHeight;
      const modelLandMark = await blazeface.load();

      if (modelLandMark && !gettingCapture) {
        const docCameraInterValId = setInterval(async () => {
          ctx.clearRect(0, 0, mediaWidth, mediaHeight);
          const predictionLandmarks = await modelLandMark.estimateFaces(selfioeWithDocVideo, false);
          const resizedDetections = window.faceapi.resizeResults(
            predictionLandmarks,
            videoConstraints
          );

          if (resizedDetections.length > 0) {
            // testing and deleteing
            ctx.fillStyle = 'red';
            ctx.fillRect(resizedDetections[0].topLeft[0], resizedDetections[0].topLeft[1], 5, 5);

            ctx.fillStyle = 'green';
            ctx.fillRect(
              resizedDetections[0].bottomRight[0],
              resizedDetections[0].bottomRight[1],
              5,
              5
            );

            ctx.fillStyle = 'Yellow';
            ctx.fillRect(
              resizedDetections[0].bottomRight[0],
              resizedDetections[0].topLeft[1],
              5,
              5
            );

            ctx.fillStyle = 'blue';
            ctx.fillRect(
              resizedDetections[0].topLeft[0],
              resizedDetections[0].bottomRight[1],
              5,
              5
            );
            setOpenloader(false);
            // setCaptureButtonShow(true);
            resizedDetections.forEach((prediction) => {
              ctx.beginPath();
              ctx.rect(
                prediction.topLeft[0],
                prediction.topLeft[1],
                prediction.bottomRight[0] - prediction.topLeft[0],
                prediction.bottomRight[1] - prediction.topLeft[1]
              );
              ctx.lineWidth = 5;
              ctx.strokeStyle = 'rgb(124,252,0)';
              ctx.stroke();
            });
          }
        }, 100);

        // setdocCameraInterVal({ docCameraInterValId });
        // localStorage.setItem('docCameraInterValId', docCameraInterValId);
      }
    }
  };

  const [videoConstraints, setVideoConstrain] = useState({
    width: window.innerWidth > 350 && window.innerWidth < 450 ? 400 : 640,
    height: window.innerWidth > 350 && window.innerWidth < 450 ? 400 : 480,
    facingMode: 'user'
  });

  useEffect(() => {
    // setOpenLoader && setOpenLoader(true);
    const isSafari = window.safari !== undefined;
    const userAgent = window.navigator.userAgent;

    if (isSafari || userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)) {
      setMirrorValue(true);
      // return false;
    } else {
      setMirrorValue(false);
    }
    // by default mirror value is false
  }, []);

  return (
    <>
      <Loader openloader={openloader} />
      <Stack>
        {/* <Webcam ref={webcamRef} screenshotFormat="image/jpeg" /> */}
        <Box className="containerStyle">
          <Box className="cameraHeading">{t('Selfie')}</Box>
          <IconButton
            size="small"
            className="closeMarkStyle"
            onClick={(e) => {
              iconButtonHandler(e);
            }}
          >
            <MdOutlineClose size={24} />
          </IconButton>
          <Box className="docSelfieComp">
            <Webcam
              onUserMedia={(userMedia) => openCvReady(userMedia)}
              // onUserMediaError={(userMediaStreamError) => {
              //   if (userMediaStreamError) {
              //     setRefreshComp(new Date());
              //   }
              // }}
              ref={selfioeWithDocRef}
              videoConstraints={videoConstraints}
              style={{ marginBottom: '2rem', opacity: 1 }}
              className="docSelfieCamOpenCv"
              id="selfioeWithDocVideo"
            />

            <canvas
              id="canvas_output"
              ref={selfioeWithDocCanvasRef}
              className="docSelfieCameraOpenCv"
            />
            <div className="overlayRecBorderCamera">{recBorder()} </div>

            <Box className="cameraIconBox" onClick={handleClickOpen}>
              <Typography variant="span" className="cameraIcon">
                <IconButton
                  size="small"
                  className="cameraIconButton"
                  onClick={(e) => {
                    // cameraSoundRef.current.play();
                    // capture(e, docCameraInterVal);
                    capture(docCameraInterVal);
                    setGetingCapture(true);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <MdCameraAlt size={20} />
                </IconButton>
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* <Box className="cameraIconBox">
          <Box onClick={handleClickOpen}>
            <Typography className="cameraIconLink" onClick={capture} sx={{ cursor: 'pointer' }}>
              <Typography variant="span" className="cameraIcon">
                <AiFillCamera />
              </Typography>
            </Typography>
          </Box>
        </Box> */}

        {/* <Button className="mainBtn" onClick={capture}>Capture photo</Button> */}
        <Box className="cameraSound" style={{ opacity: 0, position: 'absolute' }}>
          <audio controls ref={cameraSoundRef}>
            <source src={`${BASE_URL}/assets/iphone-camera-capture-6448.mp3`} type="audio/mp3" />
            <source src={`${BASE_URL}/assets/iphone-camera-capture-6448.mp3`} type="audio/mp3" />
            <source src={`${BASE_URL}/assets/iphone-camera-capture-6448.ogg`} type="audio/ogg" />
            Your browser does not support the audio element.
          </audio>
        </Box>
        {imgSrc && (
          <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="popupOuter"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description" className="">
                <img src={imgSrc} className="imageCaptured" />
              </DialogContentText>
            </DialogContent>
            <DialogActions className="popupFooter">
              {/* <Link className="mainBtn" to="/in/document-with-selfie-preview">
                Continue
              </Link> */}
              <Link className="mainBtn" onClick={() => handleDocSelfieContinue(imgSrc)}>
                {t('Continue')}
              </Link>
              <Button className="mainBtn" onClick={handleClose}>
                {t('New Shoot')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Stack>
    </>
  );
}

export default WebcamCaptureDocWithSelfie;
